import { FC, useState, ChangeEvent, useMemo } from 'react';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Selectors
import { selectQuestion, selectQuiz } from 'store/quiz/quizSelectors';
// Models
import { IAnswerResult } from 'models/Answer';
// Types
import QuizTypes from 'types/QuizTypes';
// Component
import { Button } from 'components/Buttons';
import { Input } from 'components/Controls';
// Selectors
import { setAnswers, setQuestionEnd, setQuestionStart } from 'store/player/playerSlice';
import { selectQuestionEnd } from 'store/player/playerSelectors';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { isCorrectAnswer } from 'utilities/Utilities';

type Props = {
  score: number;
  time: number;
  disabled?: boolean;
};

const TextAnswer:FC<Props> = ({ score, time, disabled = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const quiz = useAppSelector(selectQuiz);
  const question = useAppSelector(selectQuestion);
  const questionEnd = useAppSelector(selectQuestionEnd);

  const isScoringQuizzes = [QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type);

  const correctAnswers = useMemo(() => question.config?.correctAnswers || [], [question.config]);

  const [ clicked, setClicked ] = useState<boolean>(false);
  const [ title, setTitle ] = useState<string>('');
  const [ correct, setCorrect ] = useState<boolean | undefined>(undefined);

  const handleTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setCorrect(undefined);
  }

  const handleSubmit = () => {
    if (clicked) return;

    const correct = isCorrectAnswer(title, correctAnswers);

    if (quiz.type !== QuizTypes.Prediction) {
      setCorrect(correct);
      if (!correct) return;
    }

    const nextAnswer:IAnswerResult = {
      type: question.type,
      score,
      correct,
      time,
      question: {
        ref: question.ref,
        title: question.title
      },
      input: {
        text: title.trim()
      }
    }

    dispatch(setAnswers([nextAnswer]));
    dispatch(setQuestionStart(false));
    dispatch(setQuestionEnd(true));

    setClicked(true);
  }

  const skipQuestion = () => {
    setClicked(true);
    dispatch(setQuestionStart(false));
    dispatch(setQuestionEnd(true));
  }

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !questionEnd && !clicked) {
      handleSubmit();
    }
  };

  const correctColor = quiz.questionsSettings.correctAnswerColor;
  const incorrectColor = quiz.questionsSettings.incorrectAnswerColor;

  const color = correct !== undefined && isScoringQuizzes ? 
    correct
      ? correctColor
      : incorrectColor
    : undefined;

  const correctAnswer = correctAnswers[0];
  const showCorrectAnswer = !correct && correctAnswer && questionEnd && quiz.questionsSettings.highlightCorrectAnswer;

  return (
    <div>
      {correct === false ? (
        <p className="text-lg font-bold text-primary mb-4 text-center">Try again</p>
      ) : null}
      <div className={`
        mb-4 flex
        ${isScoringQuizzes ? 'flex-row gap-2 items-center' : 'flex-col'}
      `}>
        <Input
          name="question-free-answer" id="question-free-answer" placeholder={t('labels.typeAnswerHere')}
          value={title}
          onChange={handleTitle}
          disabled={disabled || clicked || questionEnd}
          onKeyPress={onKeyPress}
          style={{ color }}
          className="text-2xl"
        />
        <Button
          className={`text-xl uppercase primary-button ${isScoringQuizzes ? 'w-24 mb-4' : 'w-full'}`}
          onClick={handleSubmit}
          disabled={!title.trim() || clicked || questionEnd}
        >
          {isScoringQuizzes ? t('buttons.go') : t('buttons.next')}
        </Button>
      </div>
      {isScoringQuizzes && showCorrectAnswer ? (
        <p
          className="text-lg font-bold mb-8 text-center"
          style={{ color: quiz.questionsSettings.correctAnswerColor }}
        >
          The correct answer is: {correctAnswer}
        </p>
      ) : null}
      {isScoringQuizzes ? (
        <Button
          className="w-full text-xl uppercase primary-button"
          onClick={skipQuestion}
          disabled={clicked || questionEnd}
        >
          {t('buttons.passQuestion')}
        </Button>
      ) : null}
    </div>
  )
}

export default TextAnswer;
